/**
 * Landing-page engagement tracking: section_view, nav_click, social_click, magazin_click.
 */
import { trackEvent } from './ga4';

const SECTION_SELECTOR = '[data-ga-section]';
const SOCIAL_SELECTOR =
  'a[data-network], a[href*="instagram.com"], a[href*="facebook.com"], a[href*="tiktok.com"]';

const seenSections = new Set();
let clickBound = false;

function sectionName(el) {
  return el.getAttribute('data-ga-section') || el.id || '';
}

function initSectionViews() {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }
  const sections = document.querySelectorAll(SECTION_SELECTOR);
  if (!sections.length) {
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          return;
        }
        const name = sectionName(entry.target);
        if (!name || seenSections.has(name)) {
          return;
        }
        seenSections.add(name);
        trackEvent('section_view', { section_name: name });
        observer.unobserve(entry.target);
      });
    },
    { threshold: [0.5] }
  );
  sections.forEach((section) => observer.observe(section));
}

function resolveLinkLocation(el) {
  if (el.closest('.nav, header.nav, #nav-mobile-panel')) {
    return 'header';
  }
  if (el.closest('footer.footer, .footer')) {
    return 'footer';
  }
  return 'content';
}

function resolveNetwork(el) {
  const explicit = el.getAttribute('data-network');
  if (explicit) {
    return explicit;
  }
  const href = (el.getAttribute('href') || '').toLowerCase();
  if (href.includes('instagram.com')) {
    return 'instagram';
  }
  if (href.includes('facebook.com')) {
    return 'facebook';
  }
  if (href.includes('tiktok.com')) {
    return 'tiktok';
  }
  return null;
}

function isMagazinHref(href) {
  if (!href) {
    return false;
  }
  try {
    const url = new URL(href, window.location.origin);
    return url.pathname === '/magazin' || url.pathname === '/magazin/';
  } catch (e) {
    return href === '/magazin' || href.startsWith('/magazin?');
  }
}

function linkText(el) {
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

function isHeaderNavLink(anchor) {
  if (anchor.closest('.nav-links')) {
    return true;
  }
  if (!anchor.closest('#nav-mobile-panel')) {
    return false;
  }
  // Exclude Login and other auth CTAs in the mobile sheet.
  if (anchor.classList.contains('nav-mobile-signin')) {
    return false;
  }
  if ((anchor.getAttribute('href') || '').includes('login=true')) {
    return false;
  }
  return true;
}

function onDocumentClick(event) {
  const anchor = event.target.closest('a[href]');
  if (!anchor) {
    return;
  }

  if (isHeaderNavLink(anchor)) {
    const text = linkText(anchor);
    if (text) {
      trackEvent('nav_click', { link_text: text });
    }
  }

  if (isMagazinHref(anchor.getAttribute('href'))) {
    trackEvent('magazin_click', {
      link_location: resolveLinkLocation(anchor),
      link_text: linkText(anchor) || 'Magazin',
    });
  }

  const network = resolveNetwork(anchor);
  if (network && (anchor.matches(SOCIAL_SELECTOR) || anchor.closest('.ft-social-row'))) {
    trackEvent('social_click', {
      network,
      link_location: resolveLinkLocation(anchor),
      link_url: anchor.href,
    });
  }
}

export function initLandingAnalytics() {
  if (typeof document === 'undefined') {
    return;
  }
  initSectionViews();
  if (!clickBound) {
    clickBound = true;
    document.addEventListener('click', onDocumentClick, true);
  }
}
